<script setup lang="ts">
/**
 * Full-screen quiz layer (Figma Quizzes: "Full-screen quiz (not a modal)").
 *
 * Teleported over the page, `color/bg/page` behind. Escape and system Back do not
 * close it directly — they emit `close-request`, and the flow decides (most show
 * the Leave quiz modal first).
 */
import { onBeforeUnmount, watch } from 'vue'
import {
  isTopFairDialog,
  pushFairDialog,
  removeFairDialog,
} from '@/ui/primitives/FairDialog/fairDialogStack'
import { useFairOverlay } from '@/ui/primitives/FairOverlay'
import { popFairQuizEntry, pushFairQuizEntry, isFairQuizEntryOnTop } from './fairQuizHistory'

defineOptions({ inheritAttrs: false })

interface FairQuizLayerProps {
  open?: boolean
  ariaLabel?: string
}

const props = withDefaults(defineProps<FairQuizLayerProps>(), {
  open: false,
  ariaLabel: undefined,
})

const emit = defineEmits<{
  'close-request': []
}>()

const stackId = Symbol('fair-quiz')

useFairOverlay(() => props.open)
const historyId = `quiz-${Math.random().toString(36).slice(2)}`

function handleKeydown(event: KeyboardEvent) {
  if (event.key !== 'Escape' || !isTopFairDialog(stackId)) return
  event.stopPropagation()
  emit('close-request')
}

function handlePopState() {
  if (!props.open || isFairQuizEntryOnTop(historyId)) return
  // Back left our entry: restore it so a second Back asks again instead of leaving.
  pushFairQuizEntry(historyId)
  emit('close-request')
}

function attach() {
  pushFairDialog(stackId)
  window.addEventListener('keydown', handleKeydown, true)
  window.addEventListener('popstate', handlePopState)
  pushFairQuizEntry(historyId)
}

function detach() {
  removeFairDialog(stackId)
  window.removeEventListener('keydown', handleKeydown, true)
  window.removeEventListener('popstate', handlePopState)
}

watch(
  () => props.open,
  (open, wasOpen) => {
    if (typeof window === 'undefined') return
    if (open) {
      attach()
    } else if (wasOpen) {
      detach()
      void popFairQuizEntry(historyId)
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  if (typeof window === 'undefined' || !props.open) return
  detach()
  void popFairQuizEntry(historyId)
})
</script>

<template>
  <Teleport to="body">
    <Transition name="fair-quiz-fade">
      <div
        v-if="open"
        v-bind="$attrs"
        class="fair-quiz"
        role="dialog"
        aria-modal="true"
        :aria-label="ariaLabel"
      >
        <slot />
      </div>
    </Transition>
  </Teleport>
</template>
